/**
 * Validação de arquivo **antes do envio** (R19.5).
 *
 * Regras puras e testáveis, sem dependência de DOM/rede:
 * - o formato deve estar entre os suportados pela API (PDF/DOCX/MD/TXT/HTML) e,
 *   além disso, permitido no plano escolhido pelo usuário (R13.5);
 * - o tamanho não pode exceder o limite do plano (R1.6/R13.3);
 * - arquivos vazios são rejeitados com mensagem clara.
 *
 * Toda mensagem é em pt-BR, clara e **acionável** (diz o que fazer a seguir),
 * sem jargão técnico e sem expor detalhes internos.
 */

import {
  EXTENSION_TO_FORMAT,
  FORMAT_LABELS,
  type SourceFormat,
  type TierInfo,
} from "../config/tiers";

/** Resultado de validação: ou o arquivo é válido (com o formato resolvido), ou há um erro. */
export type ValidationResult =
  | { readonly ok: true; readonly format: SourceFormat }
  | { readonly ok: false; readonly code: ValidationErrorCode; readonly message: string };

export type ValidationErrorCode =
  | "EMPTY_FILE"
  | "UNKNOWN_EXTENSION"
  | "FORMAT_NOT_IN_TIER"
  | "FILE_TOO_LARGE";

/** Extrai a extensão (minúscula, sem ponto) de um nome de arquivo. */
export function extensionOf(filename: string): string {
  const trimmed = filename.trim();
  const dot = trimmed.lastIndexOf(".");
  if (dot < 0 || dot === trimmed.length - 1) {
    return "";
  }
  return trimmed.slice(dot + 1).toLowerCase();
}

/** Resolve o `source_format` da API a partir do nome do arquivo (ou `null`). */
export function formatFromFilename(filename: string): SourceFormat | null {
  const ext = extensionOf(filename);
  return EXTENSION_TO_FORMAT[ext] ?? null;
}

/** Formata um número de bytes em uma string humana (ex.: "4,7 MB"). */
export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) {
    return "0 B";
  }
  const units = ["B", "KB", "MB", "GB"];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  const rounded = unit === 0 ? value : Math.round(value * 10) / 10;
  // pt-BR usa vírgula como separador decimal.
  return `${rounded.toString().replace(".", ",")} ${units[unit]}`;
}

/** Lista legível dos formatos permitidos no plano (ex.: "Markdown, HTML e Texto"). */
export function allowedFormatsLabel(tier: TierInfo): string {
  const labels = tier.formats.map((f) => FORMAT_LABELS[f]);
  if (labels.length <= 1) {
    return labels.join("");
  }
  return `${labels.slice(0, -1).join(", ")} e ${labels[labels.length - 1]}`;
}

/**
 * Valida um arquivo (nome + tamanho) contra o plano do usuário.
 *
 * Recebe apenas os campos necessários (`name`, `size`) para permanecer testável
 * sem um `File` real. Retorna um `ValidationResult` discriminado.
 */
export function validateFile(
  file: { readonly name: string; readonly size: number },
  tier: TierInfo,
): ValidationResult {
  if (file.size <= 0) {
    return {
      ok: false,
      code: "EMPTY_FILE",
      message:
        "O arquivo parece estar vazio. Verifique se escolheu o documento certo e tente novamente.",
    };
  }

  const format = formatFromFilename(file.name);
  if (format === null) {
    return {
      ok: false,
      code: "UNKNOWN_EXTENSION",
      message: `Não reconhecemos esse tipo de arquivo. Envie um documento em ${allowedFormatsLabel(
        tier,
      )}.`,
    };
  }

  if (!tier.formats.includes(format)) {
    return {
      ok: false,
      code: "FORMAT_NOT_IN_TIER",
      message: `O formato ${FORMAT_LABELS[format]} não está disponível no seu plano ${tier.label}. Formatos disponíveis: ${allowedFormatsLabel(
        tier,
      )}.`,
    };
  }

  if (file.size > tier.maxFileBytes) {
    return {
      ok: false,
      code: "FILE_TOO_LARGE",
      message: `O arquivo tem ${formatBytes(
        file.size,
      )}, acima do limite de ${formatBytes(
        tier.maxFileBytes,
      )} do plano ${tier.label}. Reduza o tamanho do documento ou use um plano maior.`,
    };
  }

  return { ok: true, format };
}
